import { Component, Inject, Input, OnDestroy, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PaypointService } from '../../../services/paypoint.service';
import { Subscription } from 'rxjs';
import { FormUtility } from '../../../../../shared/utils';
import {
  PaymentProcessService,
  PaymentProcessState,
} from '../../../services/payment-process.service';
import { PaypointResponse } from '../../../models/paypoint-response.interface';
import { FinancialResponsibleContact } from '../../../models/contact.interface';
import { take } from 'rxjs/operators';

@Component({
  selector: 'kt-payment-details-tab',
  templateUrl: './payment-details-tab.component.html',
  styleUrls: ['./payment-details-tab.component.scss'],
})
export class PaymentDetailsTabComponent implements OnInit, OnDestroy {
  paymentForm!: FormGroup;
  private subscriptions = new Subscription();
  state: PaymentProcessState;
  paymentPoint: PaypointResponse;
  entitiesOptions: string[] = [];
  financialOptions: FinancialResponsibleContact[] = [];
  tabs: string[] = ['Details', 'Method', 'Review'];
  private activateSave = false;

  @Output() customerId = new EventEmitter<string>();

  @Input()
  set triggerSave(value: boolean) {
    if (this.activateSave) {
      this.save();
    }
  }

  constructor(
    private fb: FormBuilder,
    private formUtility: FormUtility,
    private paymentProcessService: PaymentProcessService,
    private payPointService: PaypointService,
    public dialogRef: MatDialogRef<PaymentDetailsTabComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      organization: string;
      office: any;
      customerId: string;
      companyId: number;
      taskId: string;
      taskEntities: any;
    },
  ) {}

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit(): void {
    this.initializeForm();
    this.entitiesOptions = this.data.taskEntities;
    this.paymentProcessService.state$.pipe(take(1)).subscribe((state) => {
      if (state) {
        this.state = state;
        this.getFormControl('entities').setValue(this.state?.entity);
        this.getFormControl('financialContact').setValue(this.state?.contact);
        this.getFormControl('amount').setValue(this.state?.amount);
      }
    });

    this.subscriptions.add(
      this.payPointService
        .getOfficePaypoints(this.data.organization, this.data.office.officeUUID)
        .subscribe((res) => {
          const data = res?.data || null;
          if (data) this.paymentPoint = data[0];
          else this.paymentPoint = null;
        }),
    );
    this.subscriptions.add(
      this.getFormControl('entities').valueChanges.subscribe((entity) => {
        if (entity?.id)
          this.payPointService
            .getBillingContact(this.data.companyId, [entity?.id])
            .subscribe((res) => {
              this.financialOptions = res.data;
            });
      }),
    );

    this.subscriptions.add(
      this.getFormControl('financialContact').valueChanges.subscribe((financialContact) => {
        if (financialContact?.id) this.customerId.emit(financialContact?.contactUUID);
      }),
    );
  }

  private initializeForm(): void {
    this.paymentForm = this.fb.group({
      amount: ['', [Validators.required, Validators.min(0.01)]],
      entities: ['', [Validators.required]],
      financialContact: ['', [Validators.required]],
      office: [this.data.office.name],
      customerId: [''],
    });
  }

  save(): boolean {
    this.paymentForm.markAllAsTouched();
    Object.values(this.paymentForm.controls).forEach((c) => c.updateValueAndValidity());
    if (this.paymentForm.invalid) return;
    this.paymentProcessService.updateField('office_id', this.data.office.officeUUID);
    this.paymentProcessService.updateField('entity', this.getFormControl('entities').value);
    this.paymentProcessService.updateField(
      'contact',
      this.getFormControl('financialContact').value,
    );
    this.paymentProcessService.updateField('amount', this.getFormControl('amount').value);
    return true;
  }

  cancel(): void {
    this.dialogRef.close(); // Close the dialog without sending data
  }

  formatEntity(entity: any): string {
    return entity?.first_name + ' ' + entity.last_name || '';
  }

  formatFinancialContact(financialContact: FinancialResponsibleContact): string {
    return financialContact?.firstName + ' ' + financialContact?.lastName || '';
  }

  getFormControl(name: string): FormControl {
    return this.formUtility.getFormControl(this.paymentForm, name);
  }
}
